import { Injectable } from '@angular/core';

/**
 * How long verification-management keeps an authenticator code spent: its
 * ±30-second acceptance window, rounded up to 90 seconds.
 */
export const AUTHENTICATOR_CODE_SPENT_MS = 90_000;

/**
 * The Google Authenticator codes this tab has already approved something
 * with. verification-management lets one code approve one action and refuses
 * it on any other (409 VERIFICATION_CODE_REUSED) — at the cost of a try. A
 * code this tab spent is caught here instead, before it is sent, so the
 * operator approving several requests in a row loses nothing.
 *
 * Memory only, never browser storage: a spent code is worthless, but it is
 * still a code, and it only has to outlive its own 90 seconds.
 */
@Injectable({ providedIn: 'root' })
export class SpentAuthenticatorCodesService {
  private readonly spentUntil = new Map<string, number>();

  remember(code: string, now: number = Date.now()): void {
    this.forgetExpired(now);
    this.spentUntil.set(code, now + AUTHENTICATOR_CODE_SPENT_MS);
  }

  isSpent(code: string, now: number = Date.now()): boolean {
    this.forgetExpired(now);
    return this.spentUntil.has(code);
  }

  private forgetExpired(now: number): void {
    for (const [code, until] of this.spentUntil) {
      if (until <= now) this.spentUntil.delete(code);
    }
  }
}
